import { useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/** Persistent banner shown across every page while the visitor is in a
 * one-tap demo session (`user.is_ephemeral`). Explains the sandbox +
 * offers a Create Free Account CTA and an Exit demo action. Hidden for
 * real users, admins, and signed-out visitors. */
export default function DemoSessionBanner() {
  const { user, exitDemoSession } = useAuth();
  const navigate = useNavigate();
  if (!user || !user.is_ephemeral) return null;

  const onCreate = async () => {
    // Exit the demo (deletes the ephemeral user + every session-scoped
    // row) then send them to /register with a clean slate.
    await exitDemoSession();
    navigate("/register", { replace: true });
  };
  const onExit = async () => {
    await exitDemoSession();
    navigate("/", { replace: true });
  };

  return (
    <div
      className="w-full bg-[hsl(var(--sb-gold-2))] text-[hsl(42_60%_96%)]"
      role="status"
      data-testid="demo-session-banner"
    >
      <div className="container-page py-2 md:py-2.5 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <Sparkles size={16} className="mt-0.5 shrink-0" />
          <p className="text-xs md:text-sm leading-snug">
            <strong>You're exploring a demo account.</strong> Changes made here won't be saved or seen by other users.
            <span className="hidden md:inline"> Create a free account to build your real profile, connect with the community, message users, and request Early Access.</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onCreate}
            className="rounded-full px-4 py-1.5 text-xs md:text-sm font-semibold bg-[hsl(42_60%_96%)] text-[hsl(var(--sb-gold-2))] hover:bg-white transition-colors whitespace-nowrap"
            data-testid="demo-banner-create-account"
          >
            Create Free Account
          </button>
          <button
            type="button"
            onClick={onExit}
            aria-label="Exit demo"
            className="rounded-full p-1.5 hover:bg-black/10 transition-colors"
            data-testid="demo-banner-exit"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
